import { useRequestMedicalContext } from "../../Contexts/RequestMedicalTestContext";
import CustomizedProgressBar from "../../Components/LinearDeterminate";
import { Checkbox, CircularProgress, Grid } from "@mui/material";
import ErrorComponent from "../../Components/ErrorComponent";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { useCallback, useEffect, useRef } from "react";
import { ScrollBar } from "../../Components/ScrollBar";
import Button from "../../Components/Button";
import { pdfjs } from "react-pdf";
import { Image } from "antd";
import dayjs from "dayjs";
import "./style.css";
import {
  KeyboardArrowLeft,
  CheckBoxOutlined,
  CloseOutlined,
} from "@mui/icons-material";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function ReqMedical2() {
  const {
    setSelectedProcedureClass,
    setSelectedProcedures,
    setMappedProcedures,
    setSelectedOrders,
    diagnosticOrders,
    handleFileChange,
    setDisplayAlert,
    procedureClass,
    selectedOrders,
    setIsLoading,
    compileToPDF,
    handleBack,
    removeFile,
    getOrders,
    isLoading,
    loadState,
    navigate,
    files,
  } = useRequestMedicalContext();

  const handleChange = (data) => {
    const isSelected = selectedOrders?.some(
      (order) =>
        order.title === data.title &&
        order.date === data.date &&
        order.details === data.details
    );

    if (isSelected) {
      // If already selected, remove it
      setSelectedOrders((prevOrders) =>
        prevOrders.filter(
          (order) =>
            !(
              order.title === data.title &&
              order.date === data.date &&
              order.details === data.details
            )
        )
      );
    } else {
      // If not selected, add it
      setSelectedOrders((prevOrders) => [...prevOrders, data]);
    }
  };

  const prevSelectedOrdersRef = useRef(selectedOrders);

  // Function to map procedures based on selectedOrders and procedureClass
  const mapProcedures = useCallback(() => {
    return procedureClass
      ?.flatMap((pc) => {
        const coveredProcedures = [];
        const notCoveredProcedures = [];

        // Sets to keep track of procedure names that have been added
        const coveredProcedureNames = new Set();
        const notCoveredProcedureNames = new Set();

        selectedOrders.forEach((order) => {
          order.coveredProcedures.forEach((proc) => {
            if (
              pc.name === `DIAGNOSTICS : ${proc.class}` &&
              !coveredProcedureNames.has(proc.name)
            ) {
              coveredProcedures.push({
                ...proc,
                orderId: order.id,
              });
              coveredProcedureNames.add(proc.name);
            }
          });

          order.notCoveredProcedures.forEach((proc) => {
            if (
              pc.name === `DIAGNOSTICS : ${proc.class}` &&
              !notCoveredProcedureNames.has(proc.name)
            ) {
              notCoveredProcedures.push({
                ...proc,
                orderId: order.id,
              });
              notCoveredProcedureNames.add(proc.name);
            }
          });
        });

        if (coveredProcedures.length > 0 || notCoveredProcedures.length > 0) {
          const procedureClassList = [
            "ULTRASOUND PROCEDURES",
            "X-RAY PROCEDURES",
            "OB-SONO PROCEDURES",
          ];
          const isSpecialClass = procedureClassList.includes(
            pc.name.split(" : ")[1]
          );

          if (isSpecialClass && coveredProcedures.length > 1) {
            // Split covered procedures into separate entries
            return coveredProcedures.map((proc) => ({
              name: pc.name,
              coveredProcedures: [proc],
              notCoveredProcedures: [],
              orderId: proc.orderId,
            }));
          } else {
            return [
              {
                name: pc.name,
                coveredProcedures: coveredProcedures,
                notCoveredProcedures: notCoveredProcedures,
                orderId:
                  coveredProcedures.length > 0
                    ? coveredProcedures[0].orderId
                    : notCoveredProcedures[0].orderId,
              },
            ];
          }
        }
        return [];
      })
      .filter((item) => item !== null);
  }, [procedureClass, selectedOrders]);

  // Update selectedProcedures when selectedOrders changes
  useEffect(() => {
    const hasSelectedOrdersChanged =
      JSON.stringify(prevSelectedOrdersRef.current) !==
      JSON.stringify(selectedOrders);

    if (hasSelectedOrdersChanged) {
      setSelectedProcedures(mapProcedures());
      prevSelectedOrdersRef.current = selectedOrders;
    }
  }, [selectedOrders, mapProcedures]);

  const handleNext = async () => {
    if (!stepTwoValidation) return;

    if (files.length > 0) {
      setIsLoading(true);
      try {
        await compileToPDF();
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
    setSelectedProcedureClass([]);
    setMappedProcedures(mapProcedures());
    navigate("/request-medical-test/procedure");
  };

  const stepTwoValidation =
    files.length > 0 ? files.length > 0 : selectedOrders.length > 0;

  const handleKeyDown = (event, callback) => {
    if (event.key === "Enter" || event.key === " ") {
      callback();
    }
  };

  let content = null;
  if (loadState === "get orders") {
    content = (
      <div className="w-full flex justify-center items-center mt-4">
        <CircularProgress />
      </div>
    );
  } else if (
    ["get orders ISE", "get orders timeout", "get orders retry"].includes(
      loadState
    )
  ) {
    content = (
      <ErrorComponent
        onRetry={() => getOrders("get orders retry")}
        isLoading={loadState === "get orders retry"}
      />
    );
  } else if (diagnosticOrders?.length > 0) {
    content = (
      <>
        <div className="text-sm text-[#60656D]">
          Select the diagnostic order below for this medical test
        </div>
        {diagnosticOrders.map((data) => (
          <div key={data} className="flex flex-row my-2">
            <label id="labl" className="w-full mt-1">
              <input
                name="diagnosticorder"
                type="checkbox"
                onChange={() => handleChange(data)}
                checked={selectedOrders?.some(
                  (order) =>
                    order.title === data.title &&
                    order.date === data.date &&
                    order.details === data.details
                )}
                disabled={files.length > 0}
              />
              <div
                className={`flex w-full text-sm p-3 border rounded-lg ${
                  files.length > 0 ? null : "cursor-pointer"
                }`}
              >
                <div>
                  <Checkbox
                    icon={<CheckBoxOutlined />}
                    checkedIcon={<CheckBoxOutlined />}
                    sx={{
                      padding: 0,
                      color: "#E7EEFA",
                      "&.Mui-checked": {
                        color: "#0947AB",
                      },
                    }}
                    onChange={() => handleChange(data)}
                    checked={selectedOrders?.some(
                      (order) =>
                        order.title === data.title &&
                        order.date === data.date &&
                        order.details === data.details
                    )}
                    disabled={files.length > 0}
                  />
                </div>
                <div className="pl-1">
                  <p>{data.title}</p>
                  <p>{dayjs(data.date).format("MMMM DD, YYYY")}</p>
                </div>
              </div>
            </label>
          </div>
        ))}
      </>
    );
  } else {
    content = (
      <div className="text-sm text-[#60656D]">
        You currently don't have any diagnostic orders on your account
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen sm:items-center sm:bg-[#E7EEFA]">
      <div className="flex flex-col flex-1 sm:flex-none bg-white sm:w-148 sm:items-center rounded-xl sm:my-24">
        <div className="w-full">
          <div className="flex flex-row p-3">
            <KeyboardArrowLeft
              className="cursor-pointer"
              onClick={handleBack}
            />
            <div className="text-center text-md font-bold flex-1">
              Request Medical Test
            </div>
            <CloseOutlined
              className=" cursor-pointer"
              onClick={() => setDisplayAlert(true)}
            />
          </div>
          <div className="p-3">
            <CustomizedProgressBar value={28.56} />
          </div>
        </div>
        <div className="flex flex-col w-full">
          <div className="px-3">
            <div className="text-lg font-bold">Diagnostic orders</div>
            {content}
          </div>
        </div>
        <div className="flex flex-col items-center mt-2">
          <input
            type="file"
            accept="image/*,.pdf"
            multiple
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            disabled={selectedOrders.length > 0}
          />
          <label htmlFor="file-upload">
            <div
              className={`border rounded-md px-3 py-1 ${
                selectedOrders.length > 0
                  ? "text-[#A3A7AB] bg-[#F6F7F7]"
                  : "bg-white text-black cursor-pointer"
              }`}
              disabled={selectedOrders.length > 0}
            >
              Upload
            </div>
          </label>
        </div>
        {files.length > 0 ? (
          <ScrollArea.Root className="w-full px-3 rounded overflow-hidden">
            <ScrollArea.Viewport className="w-full min-h-[200px] sm:h-full sm:max-h-[400px] rounded">
              <div className="flex flex-col items-center mt-2">
                <Grid
                  container
                  spacing={2}
                  className="justify-center"
                  sx={{ marginTop: 2, marginLeft: 0, width: "100%" }}
                >
                  <Image.PreviewGroup
                    preview={{
                      onChange: (current, prev) =>
                        console.log(
                          `current index: ${current}, prev index: ${prev}`
                        ),
                    }}
                  >
                    {files.map((file, index) => (
                      <Grid item key={file}>
                        <div className="flex flex-col items-center mr-2">
                          <button
                            className="bg-[#f7f7f7] border w-full text-center mb-1"
                            onClick={() => removeFile(index)}
                            onKeyDown={(event) =>
                              handleKeyDown(event, () => removeFile(index))
                            }
                          >
                            Remove
                          </button>
                          <Image
                            className="border bg-cover bg-center"
                            height={"6rem"}
                            width={"6rem"}
                            src={file}
                          />
                        </div>
                      </Grid>
                    ))}
                  </Image.PreviewGroup>
                </Grid>
              </div>
            </ScrollArea.Viewport>
            <ScrollBar />
          </ScrollArea.Root>
        ) : null}
        <div className="flex w-full p-4 mt-auto sm:justify-end">
          <Button
            disabled={!stepTwoValidation || isLoading}
            className={`h-14 w-full sm:w-22.5 rounded-xl ${
              !stepTwoValidation || isLoading ? "bg-[#F6F7F7]" : "bg-[#0053CC]"
            }`}
            onClick={handleNext}
          >
            <p
              className={`font-medium font-sans text-xs ${
                !stepTwoValidation || isLoading
                  ? "text-[#A3A7AB]"
                  : "text-white"
              }`}
            >
              {isLoading ? <CircularProgress size={"1rem"} /> : "Continue"}
            </p>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ReqMedical2;
